import { Injectable } from '@angular/core';
import { Auth0Client, GetTokenSilentlyOptions, User } from '@auth0/auth0-spa-js';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { IAuthProvider, IdentityProvider } from '~models';

const sessionStorageCache = {
  get: (key: string) => {
    return JSON.parse(sessionStorage.getItem(key));
  },

  set: (key: string, value: unknown) => {
    sessionStorage.setItem(key, JSON.stringify(value));
  },

  remove: (key: string) => {
    sessionStorage.removeItem(key);
  },
};

@Injectable({
  providedIn: 'root',
})
export class Auth0Service implements IAuthProvider {
  private auth0: Auth0Client;
  private user: User;
  public readonly identityProvider = IdentityProvider.Okta;

  constructor(private cookieService: CookieService) {
    this.auth0 = new Auth0Client({
      domain: environment.zipAuth0.domain,
      clientId: environment.zipAuth0.clientId,
      authorizationParams: {
        redirect_uri: `${window.location.origin}/au/giftcards/auth/callback`,
        audience: environment.zipAuth0.audience,
        scope: environment.zipAuth0.scope,
      },
      cache: sessionStorageCache,
    });
  }

  public async afterAuth() {
    this.user = await this.auth0.getUser();
  }

  public async redirectToLogin(returnTo: string) {
    await this.auth0.loginWithRedirect({ appState: { returnTo } });
  }

  private async getTokenSilently() {
    try {
      return await this.auth0.getTokenSilently();
    } catch (error) {
      if (error?.error !== 'login_required') {
        window.zbm.reportCustomError(
          'AUTH0_SILENT_GET_TOKEN_FAILED',
          error?.error,
          this.cookieService.get('zip_id'),
        );
      }
      return undefined;
    }
  }

  public async getAccessToken() {
    return (await this.isAuthenticated()) ? await this.getTokenSilently() : undefined;
  }

  public async silentRefresh() {
    return (await this.getTokenSilently()) ? true : false;
  }

  public async handleCallback() {
    try {
      const result = await this.auth0.handleRedirectCallback();
      return result.appState.returnTo ?? '/';
    } catch (error) {
      window.zbm.reportCustomError(
        'AUTH0_CALLBACK_FAILED',
        error?.error,
        this.cookieService.get('zip_id'),
      );
      throw error;
    }
  }

  public async isAuthenticated() {
    return await this.auth0.isAuthenticated();
  }

  public get customerId() {
    return this.user?.customer_id ?? this.user?.sub?.replace('auth0|', '');
  }
}
